import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import {
  Bell,
  BellOff,
  Package,
  CheckCircle,
  XCircle,
  Undo2,
  Search,
  Eye,
  Heart,
  MessageSquare,
  MessageSquareText,
  Trophy,
} from 'lucide-react';
import { useUnreadCount, useNotifications, useMarkAsRead, useMarkAllAsRead } from '../../hooks/useNotifications';
import { useTranslation } from '../../i18n';
import type { Notification, NotificationType } from '@siliconvault/shared';
import styles from './NotificationBell.module.css';

function getNotifIcon(type: NotificationType) {
  switch (type) {
    case 'offer_received': return Package;
    case 'offer_accepted': return CheckCircle;
    case 'offer_declined': return XCircle;
    case 'offer_withdrawn': return Undo2;
    case 'wanted_match': return Search;
    case 'profile_viewed': return Eye;
    case 'item_favorited': return Heart;
    case 'catalog_milestone': return Trophy;
    case 'message_received' as any: return MessageSquare;
    case 'feedback_received': return MessageSquareText;
    case 'feedback_reply': return MessageSquareText;
    default: return Bell;
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

function getNotifLink(notif: Notification): string | null {
  const data = notif.data;
  switch (notif.type) {
    case 'offer_received':
    case 'offer_accepted':
    case 'offer_declined':
    case 'offer_withdrawn':
      return data.offerId ? `/offers/${data.offerId}` : '/offers';
    case 'wanted_match':
      return data.itemId ? `/inventory/${data.itemId}?from=showroom` : '/showroom';
    case 'item_favorited':
      return data.itemId ? `/inventory/${data.itemId}` : '/showroom';
    case 'message_received' as any:
      return '/messages';
    case 'profile_viewed':
      return data.userId ? `/profile/${data.userId}` : null;
    case 'catalog_milestone':
      return '/settings';
    case 'feedback_received':
      return '/admin';
    case 'feedback_reply':
      return '/feedback';
    default:
      return null;
  }
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const bellRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [posStyle, setPosStyle] = useState<React.CSSProperties>({});

  const { data: unreadData } = useUnreadCount();
  const { data: notifsData } = useNotifications(open ? { limit: '15' } : {});
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  const unreadCount = unreadData?.unreadCount ?? 0;
  const notifications = notifsData?.data ?? [];

  const updatePos = useCallback(() => {
    if (bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const isRtl = document.documentElement.dir === 'rtl';
      const dropdownWidth = 340;
      if (isRtl) {
        // Align left edge with bell left, but clamp so it doesn't go off-screen
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - dropdownWidth - 8));
        setPosStyle({ top: rect.bottom + 6, left, right: 'auto' });
      } else {
        const right = Math.max(8, window.innerWidth - rect.right);
        setPosStyle({ top: rect.bottom + 6, right, left: 'auto' });
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current && !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, updatePos]);

  function handleNotifClick(notif: Notification) {
    if (!notif.read) {
      markAsRead.mutate(notif.id);
    }
    const link = getNotifLink(notif);
    if (link) {
      navigate({ to: link });
    }
    setOpen(false);
  }

  function handleMarkAllRead() {
    markAllAsRead.mutate();
  }

  return (
    <div className={styles.bellWrap}>
      <button
        ref={bellRef}
        className={styles.bellBtn}
        onClick={() => setOpen(!open)}
        aria-label={t('notifications.title')}
      >
        <Bell size={15} strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          style={posStyle}
        >
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownTitle}>{t('notifications.title')}</span>
            {unreadCount > 0 && (
              <button className={styles.markAllBtn} onClick={handleMarkAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className={styles.notifList}>
            {notifications.length === 0 ? (
              <div className={styles.emptyState}>
                <BellOff size={28} strokeWidth={1.5} className={styles.emptyIcon} />
                <div className={styles.emptyText}>{t('notifications.empty')}</div>
                <div className={styles.emptyDesc}>{t('notifications.emptyDesc')}</div>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = getNotifIcon(notif.type);
                return (
                  <div
                    key={notif.id}
                    className={styles.notifItem}
                    onClick={() => handleNotifClick(notif)}
                  >
                    <div className={styles.notifIcon}>
                      <Icon size={14} strokeWidth={1.75} />
                    </div>
                    <div className={styles.notifContent}>
                      <div className={styles.notifTitle}>{notif.title}</div>
                      {notif.body && <div className={styles.notifBody}>{notif.body}</div>}
                      <div className={styles.notifTime}>{timeAgo(notif.createdAt)}</div>
                    </div>
                    {!notif.read && <div className={styles.unreadDot} />}
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
